<script type="text/jsx" lang="jsx">
import _ from 'lodash';
import { isEmptyVal } from '@/utils';
import { planUnitMap } from '@/common/product-unit';
import CycleCurrentBadge from './cycle-current-badge.vue';
import BilledBillingCycleRadio from "@/components/service/config-billing-cycle/billed-cycle/index.vue";
import { ZmRadio } from "@zoom/zoom-ui-vue3";
import discountedMonthPrice from '@/common/mixin/discounted-month-price';

export default {
  name: 'ConfigCycle',
  components: {
    BilledBillingCycleRadio,
    // SaveCount,
    CycleCurrentBadge,
    ZmRadio
  },
  mixins: [discountedMonthPrice],
  model: {
    prop: 'billingCycle',
    event: 'syncCycle',
  },
  props: {
    // billing cycle plan list
    billingCycleList: {
      type: Array,
      required: true,
      default: () => [],
    },
    billingCycle: String,
    modelValue: String,
    // Current license count for downsell calculation
    currentLicense: Number,
    showCycleAlert: Boolean,
    showMonthlyPriceFromCycle: Boolean,
    unit: {
      type: Number,
      default: planUnitMap.license,
      validator: function(v) {
        return _.chain(planUnitMap)
          .values()
          .includes(v)
          .value();
      }
    },
    trackingPlanName: {
      type: String,
      required: false,
      default: '',
    }
  },
  emits: ['update:modelValue', 'switchCycleAction', 'syncCycle'],
  computed: {
    value() {
      return this.modelValue === undefined ? this.billingCycle : this.modelValue;
    },
    currentPlanList() {
      return _.map(this.billingCycleList, (p) => p.plan);
    },
    groupByPlanListByCycle() {
      return _.groupBy(this.currentPlanList, (p) => p?.billing_cycle);
    },

    firstDisabledCycleAlertItem() {
      return _.find(this.billingCycleList, (p) => p.disabled && !isEmptyVal(p.alert));
    },
    billedBillingCycleList() {
      // provide support for BilledBillingCycleRadio component input
      return this.billingCycleList.map(item => {
        return {...item, disabledTooltip: item.tooltips || item.disabledTooltip}
      });
    },
  },
  methods: {
    getEffectivePriceDisplay(plan) {
      if (this.getPriceOfMonthWithDownsell && this.currentLicense !== undefined) {
        const { getEffectiveAmount } = require('@/utils/downsell-price');
        const context = { currentLicense: this.currentLicense };
        const effectiveAmount = getEffectiveAmount(plan, context);

        if (effectiveAmount !== plan.amount) {
          return this.$money(plan.cycleValue ? effectiveAmount / plan.cycleValue : effectiveAmount, { forceDiff: true });
        }
      }
      return this.$money(plan.cycleValue ? plan.amount / plan.cycleValue : plan.amount, { forceDiff: true });
    },
    // FREE TRIAL - abstract single unit because for free trial, it should show 'unit + 'after trial''
    mx_renderCycleInnerVNode(item = {}) {
      let { plan = {}, disabledInnerDesc = '' } = item;
      return disabledInnerDesc ?
        <p class="opc-cycle__disabled--desc">{disabledInnerDesc}</p> :
        <div class="opc-cycle__plan__price">
          <p class="opc-cycle__plan__price--amount">
            {this.getEffectivePriceDisplay(plan)}
          </p>
          <p class="opc-cycle__plan__price--unit">
            <span innerHTML={this.getMergedBilledText(plan.billing_cycle, plan.amount, this.unit)}></span> <priceVATTip />
          </p>
        </div>;
    },
    mx_renderBillingCycleCol(item = {}) {
      let { plan = {}, disabled = false, tooltips = '', alert = "" } = item;

      const planIsSelected = plan?.cartInfo?.selected_number > 0;

      // support current plan have been selected, but the current have been disabled case
      if (planIsSelected) disabled = false;

      const cycleRadioVNode = <ZmRadio
        data-testid={`checkout-radio-billing-cycle-${plan.plan_code}`}
        disabled={disabled}
        label={plan.billing_cycle}
        forward-tooltip-trigger={false}
      >
        <p class="opc-cycle__plan__title">
          <span>{this.$t(plan.billing_cycle ? `billing.billing_cycle.${plan.billing_cycle}` : '')}</span>
          <CycleCurrentBadge value={plan} />
        </p>
        {this.mx_renderCycleInnerVNode(item)}
      </ZmRadio>;

      const shouldShowTips = (this.showCycleAlert && isEmptyVal(alert) && !isEmptyVal(tooltips))
        || (!isEmptyVal(tooltips) && !this.showCycleAlert);

      if (shouldShowTips) {
        return <zm-tooltip key={plan.plan_code}>
          {{
            content: () => <p innerHTML={tooltips}></p>,
            default: () => cycleRadioVNode
          }}
        </zm-tooltip>;
      }

      return cycleRadioVNode;
    },
    mx_renderAlert(slot, title) {
      if (slot) return slot();
      if (title) return <zm-banner
        class="opc-common-info-alert"
        type="warning"
        show-close={false}
        show-icon>
        <p innerHTML={title} onClick={this.trackingAlert} />
      </zm-banner>;
    },
    mx_renderCycleDisabledAlert() {
      if (!this.showCycleAlert || !this.firstDisabledCycleAlertItem) return null;
      return this.mx_renderAlert(this.$slots?.cycleAlert, this.firstDisabledCycleAlertItem?.alert);
    },
    switchBillingCycle(oldPlan, newPlan) {
      this.$emit("switchCycleAction", oldPlan, newPlan);
    },
    changeBillingCycle(cycle) {
      this.$emit("syncCycle", cycle);
      this.$emit('update:modelValue', cycle);
    },
    trackingAlert(e) {
      const { tagName = "", className, text = "" } = e?.target || {};
      const { alert } = this.firstDisabledCycleAlertItem || {};

      if (
        tagName?.toLowerCase() === "a"
        && className === "opc-go-back__billing"
        && this.showCycleAlert
        && alert
      ) {
        this.$tracking.buyInteraction(
          `banner - product level`,
          `${this.trackingPlanName ? `${this.trackingPlanName} - `: ''}${alert} - ${text}`,
        );
      }
    },
  },
  render() {
    const classList = ['opc-card-radio', 'opc-cycle__plan', `opc-cycle__plan__${this.currentPlanList?.length || 2}`];

    const billingCycle = this.showMonthlyPriceFromCycle ?
      <billed-billing-cycle-radio
        value={this.value}
        billingCycleList={this.billedBillingCycleList || []}
        unit={this.unit}
        onChange={this.switchBillingCycle}
        onInput={this.changeBillingCycle}
        aria-label="Select a subscription cycle"
        class={classList}
      /> :
      <cycle-radio-group
        enter-select
        modelValue={this.value}
        onInput={this.changeBillingCycle}
        plans={this.currentPlanList}
        action={this.switchBillingCycle}
        aria-label="Select a subscription cycle"
        class={classList}>
        {_.map(this.billingCycleList, (p) => this.mx_renderBillingCycleCol(p))}
      </cycle-radio-group>

    return <div class="opc-cycle">
      <p id="opc-cycle__title" class="opc-cycle__title">{this.$t('billing.select_billing_cycle')}</p>
      {billingCycle}
      {this.mx_renderCycleDisabledAlert()}
    </div>;
  },
  watch: {
    firstDisabledCycleAlertItem: {
      immediate: true,
      handler(item) {
        if (this.showCycleAlert && item?.alert) {
          this.$tracking.buyInteraction(
            'banner - product level',
            `${this.trackingPlanName ? `${this.trackingPlanName} - `: ''}${item.alert} - impression`,
          );
        }
      },
    }
  }
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';
.opc-cycle {
  margin-top: 16px;

  &__title {
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    height: 100%;
    line-height: 20px;
  }

  &__plan {
    margin-top: 8px;

    &__2.opc-card-radio {
      gap: 12px;
    }

    &__3.opc-card-radio {
      gap: 12px;
    }

    .zbo-radio {
      flex: 1;
      min-width: 0;

      &__wrap {
        width: 100%;

        &.is-disabled {
          .zbo-radio__label {
            cursor: not-allowed;
            .opc-cycle__plan__title {
              color: var(--zoom-color-state-disable, #ADB1B8);
            }
            .opc-cycle__plan__price--amount,
            .opc-cycle__plan__price--unit {
              color: var(--zoom-color-state-disable, #ADB1B8);
            }
          }
        }
      }

      &__label {
        width: 100%;
        display: flex;
        flex-direction: column;

        &--inner {
          width: 100%;
          /* padding-right: 2px !important; */
        }
      }
    }

    &__title {
      font-weight: 400;
      font-size: 16px;
      text-transform: capitalize;
      display: flex;
      align-items: center;
      flex-wrap: wrap; // for i18n
      align-items: center;

      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      line-height: 20px;
    }

    &__price {
      word-break: break-word;

      &--amount, &--unit {
        word-break: break-word;
        color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      }

      &--amount {
        font-size: 24px;
        font-style: normal;
        font-weight: 590;
        line-height: 28px;
        margin: 4px 0 2px;
      }

      &--unit {
        font-size: 14px;
        font-weight: 400;
        line-height: 18px;
      }
    }
  }

  &__disabled--desc {
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    letter-spacing: 0em;
    text-align: left;
    color: var(--zoom-color-text-strong-neutral, #555B62);
    margin-top: 8px;
  }

  &__plan__save {
    margin-left: 4px;
  }
}

@include media(mobile) {
  .opc-cycle {
    margin-top: 16px;
    &__title {
      font-size: 18px;
    }
    &__plan {
      margin-top: 8px;
      &__2.opc-card-radio {
        gap: 12px;
      }
      &__3.opc-card-radio {
        gap: 12px;
      }
      &__title {
        font-size: 16px;
      }
      &__price {
        &--amount {
          font-size: 22px;
        }
        &--unit {
          font-size: 12px;
        }
      }
    }

    &__disabled--desc {
      font-size: 14px;
      line-height: 21px;
      margin-top: 8px;
    }
    &__plan__save {
      margin-left: 4px;
    }
  }
}
</style>
